import { type JSX, useCallback, useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import { Table } from "@similarweb/pro-logic-components";
import { colorsSets } from "@similarweb/styles";

import { swSettings } from "app/common/services/swSettings";
import { useApiQuery } from "app/modules/api-client";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { useKeywordLists } from "app/modules/demand-analysis/keyword-lists";
import DurationService from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import { useKeywordListsNodeAPIQueryParams } from "../../hooks/useKeywordListsNodeAPIQueryParams";
import { getSearchTrendsExcelURL } from "../../utils/getExcelURL";

import { type KeywordGroupTableRow, getDefaultColumns } from "./keywordGroupsTableColumns";
import { processSearchTrendsKeywordsData } from "./keywordGroupsTableDataSelector";

const WidgetContainerWrapper = styled(WidgetContainer)`
    width: 100%;
`;

export const KeywordGroupTable = (): JSX.Element => {
    const periodOverPeriodType = DurationService.getPeriodOverPeriodCompareType();
    const {
        params: { duration, comparedDuration },
    } = useCurrentStateAndParams();
    const { from, to, compareFrom, compareTo, isWindow } = DurationService.getDurationData(
        duration,
        comparedDuration,
    ).forAPI;
    const { getKeywordListById } = useKeywordLists();

    const queryParams = useKeywordListsNodeAPIQueryParams();
    const {
        data = [],
        isLoading,
        isError,
    } = useApiQuery(
        {
            tag: "DemandSearchTrends",
            methodName: "marketResearchDemandAnalysisSearchTrendsList",
            options: { select: processSearchTrendsKeywordsData },
        },
        queryParams,
    );
    const excelAllowed = Boolean(swSettings.current.resources.IsExcelAllowed);
    const subtitleFilters = useSubtitleFilters("date", "country", "webSource");
    const columns = getDefaultColumns({ periodOverPeriodType, from, to, compareFrom, compareTo, isWindow });
    const processedRows: KeywordGroupTableRow[] = useMemo(
        () => data.map((row) => ({ ...row, color: getKeywordListById(row.id)?.color ?? colorsSets.c.others })),
        [data, getKeywordListById],
    );

    const excelURL = getSearchTrendsExcelURL(queryParams);

    const handleExcelClick = useCallback(() => {
        allTrackers.dispatchTrackEvent(
            "demand.analysis.search.trends.breakdown.table",
            "click",
            "demand.analysis.search.trends.breakdown.table",
        );
    }, []);

    return (
        <WidgetContainerWrapper
            withBorder
            isExcelDisabled={!excelAllowed}
            automationId="search-trends-trend-summary-widget"
            title={translate("demand.analysis.search.trends.keywords.breakdown.table.title")}
            tooltip={translate("demand.analysis.search.trends.keywords.breakdown.table.title.tooltip")}
            isLoading={isLoading}
            subtitleFilters={subtitleFilters}
            excelUrl={excelURL}
            onExcelClick={handleExcelClick}
            noData={isError}
        >
            <Table
                isLoading={isLoading}
                data={processedRows}
                columnConfig={columns}
                sticky={false}
            />
        </WidgetContainerWrapper>
    );
};
